<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    tabindex="-1"
    v-if="isVisible"
  >
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-money-check-alt me-2" aria-hidden="true"></i>
            {{
              payNowMode
                ? $t("financialSummary.quickPayExpenseTitle")
                : $t("financialSummary.quickRequestPaymentTitle")
            }}
          </h5>
          <button type="button" class="btn-close" @click="close"></button>
        </div>
        <div class="modal-body">
          <p class="text-muted small mb-2">
            {{
              payNowMode
                ? $t("financialSummary.quickPayExpenseHint")
                : $t("financialSummary.quickRequestPaymentHint")
            }}
          </p>
          <div v-if="splitSupplierMode" class="mb-3">
            <label class="form-label small fw-semibold mb-1">
              {{ $t("Contact") }}
            </label>
            <FieldReference
              :field="splitContactFieldDef"
              :value="splitContactRefValue"
              :model-name="'shipping_expenses'"
              field-id="quick-split-payment-contact"
              editing-fields="contact"
              mode="edit"
              :show-label="false"
              :reference-options="splitContactReferenceOptions"
              @input="onSplitContactInput"
            />
          </div>
          <ContentSkeleton v-if="loading" variant="form" :rows="5" padded />
          <template v-else-if="quickFields.length > 0">
          <div class="quick-payment-fields">
          <Form2
            ref="paymentFieldsForm"
            :fields="quickFields"
            :value="formData"
            :lists="lists"
            :models="models"
            :modelDefinitions="modelDefinitions"
            selectedModel="payment"
            :visualGroups="[]"
            :parentID="expenseId"
            :showButtons="false"
            @input="onFormInput"
          />
          </div>
          <!-- El monto del pago y lo que se imputa no son lo mismo: se ve a qué
               gasto va y por cuánto, con el redondeo y la comisión al lado. -->
          <div
            v-if="paymentAppliedtoSummaryVisible"
            class="card border-light mt-3 mb-0"
          >
            <div class="card-body py-2 px-3">
              <div class="small text-uppercase text-muted fw-semibold mb-2">
                {{ $t("financialSummary.quickPaymentAppliedToTitle") }}
              </div>
              <div
                class="d-flex justify-content-between align-items-center gap-2 flex-wrap mb-2"
              >
                <span class="small text-body text-break">{{
                  expenseName || $t("financialSummary.quickPaymentAppliedToUnnamed")
                }}</span>
                <span class="fw-semibold text-body">${{ formattedAppliedToAmount }}</span>
              </div>
              <div v-if="paymentAppliedtoExtrasVisible" class="row g-2">
                <div class="col-md-6">
                  <label
                    class="form-label small mb-1"
                    for="qrp-appliedto-rounding"
                    >{{ $t("financialSummary.quickAddCollectAppliedtoRounding") }}</label>
                  <input
                    id="qrp-appliedto-rounding"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-control form-control-sm"
                    :value="appliedtoExtraRoundingDisplay"
                    :disabled="isSubmitting"
                    @input="onAppliedtoExtraRoundingInput"
                  />
                </div>
                <div class="col-md-6">
                  <label
                    class="form-label small mb-1"
                    for="qrp-appliedto-commission"
                    >{{ $t("financialSummary.quickAddCollectAppliedtoCommission") }}</label>
                  <input
                    id="qrp-appliedto-commission"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-control form-control-sm"
                    :value="appliedtoExtraCommissionDisplay"
                    :disabled="isSubmitting"
                    @input="onAppliedtoExtraCommissionInput"
                  />
                </div>
                <div
                  v-if="paymentAmountWithRoundingVisible"
                  class="col-12 mt-1 pt-2 border-top d-flex justify-content-between align-items-center flex-wrap gap-1"
                >
                  <span class="small text-muted mb-0">{{
                    $t("financialSummary.quickAddCollectAmountPlusRoundingTotal")
                  }}</span>
                  <span class="fw-semibold text-primary mb-0">
                    ${{ formattedPaymentAmountWithRounding }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          </template>
          <div v-else class="alert alert-warning mb-0">
            {{ $t("financialSummary.quickRequestPaymentMissingFields") }}
          </div>
        </div>
        <div
          v-if="!loading && quickFields.length > 0"
          class="modal-body border-top pt-3 pb-2"
        >
          <label class="form-label small mb-1" for="qrp-payment-comment">{{
            $t("financialSummary.quickRequestPaymentCommentLabel")
          }}</label>
          <textarea
            id="qrp-payment-comment"
            v-model="formData.comment"
            class="form-control form-control-sm"
            rows="2"
            :disabled="isSubmitting"
          ></textarea>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="close">
            {{ $t("cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="loading || isSubmitting || quickFields.length === 0"
            @click="submit"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            ></span>
            {{
              payNowMode
                ? $t("financialSummary.quickPayExpenseSave")
                : $t("financialSummary.quickRequestPaymentSave")
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi, getCurrentClientDbName } from "@/api/wencesApi";
import Swal from "sweetalert2";
import Form2 from "./Form2.vue";
import { FieldReference } from "../model-definition-core";
import {
  processReferenceFormData,
  removeReferenceGroupIds,
} from "./referenceModalFormProcessing.js";
import { formatNumberSync } from "@/utils/numberFormatter";
import { saveConfirmingContactOutsideOperation } from "@/utils/dynamicModelValidationSwal";
import {
  serializeExpenseFileForApi,
  fileFieldDefFromShippingDefinition,
} from "@/utils/shippingExpenseFileField";
import { expensesArePaidWithoutRequest } from "@/utils/expensePayMode";
import { calendarTodayLocalYyyyMmDd, toYyyyMmDdSync } from "@/utils/dateFormatter";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

const PARENT_MODEL = "shipping_expenses";

/** Orden de campos en el modal rápido (solo los que existan en el modelo `payment`). */
const QUICK_PAYMENT_FIELD_KEYS = [
  "amount",
  "bankaccount",
  "payment_request_file",
];

/** Pago directo: hace falta la fecha, porque el pago nace ya pagado. */
const QUICK_PAY_NOW_FIELD_KEYS = [
  "amount",
  "payment_date",
  "bankaccount",
  "payment_request_file",
];

function roundPaymentAmount(n) {
  return parseFloat(Number(n).toFixed(2));
}

/** Alinear montos en `payment.appliedto` con el monto total del pago (misma idea que cobro rápido). */
function syncAppliedtoAmountsWithPaymentAmount(appliedto, paymentAmount) {
  if (!Array.isArray(appliedto) || appliedto.length === 0) return appliedto;
  const target = roundPaymentAmount(paymentAmount);
  if (!Number.isFinite(target) || target < 0) return appliedto;

  const rows = appliedto.map((row) =>
    typeof row === "object" && row !== null ? { ...row } : row
  );

  const numericRows = rows.filter(
    (r) => typeof r === "object" && r !== null && "amount" in r
  );
  if (numericRows.length === 0) return appliedto;

  if (rows.length === 1 && typeof rows[0] === "object" && rows[0] !== null) {
    rows[0].amount = target;
    return rows;
  }

  const oldSum = numericRows.reduce(
    (s, row) => s + (parseFloat(row.amount) || 0),
    0
  );
  if (oldSum <= 0) {
    const each = roundPaymentAmount(target / numericRows.length);
    let distributed = 0;
    for (let i = 0; i < numericRows.length - 1; i++) {
      numericRows[i].amount = each;
      distributed += each;
    }
    numericRows[numericRows.length - 1].amount = roundPaymentAmount(
      target - distributed
    );
    return rows;
  }

  const ratio = target / oldSum;
  let acc = 0;
  for (let i = 0; i < numericRows.length - 1; i++) {
    const prev = parseFloat(numericRows[i].amount) || 0;
    const v = roundPaymentAmount(prev * ratio);
    numericRows[i].amount = v;
    acc += v;
  }
  numericRows[numericRows.length - 1].amount = roundPaymentAmount(target - acc);
  return rows;
}

/**
 * `processReferenceFormData` solo incluye subcampos del grupo `appliedto` que existen en ModelDefinition.
 * Rounding y bank_commission en payment_appliedto se reinyectan desde el merge previo al POST
 * (mismo patrón que cobro rápido).
 */
function mergePaymentAppliedtoExtrasIntoPayload(mergedSource, payload) {
  if (
    !mergedSource ||
    !payload ||
    !Array.isArray(mergedSource.appliedto) ||
    !Array.isArray(payload.appliedto)
  ) {
    return;
  }
  const n = Math.min(mergedSource.appliedto.length, payload.appliedto.length);
  for (let i = 0; i < n; i++) {
    const src = mergedSource.appliedto[i];
    const dst = payload.appliedto[i];
    if (!src || !dst || typeof src !== "object" || typeof dst !== "object") continue;
    if (Object.prototype.hasOwnProperty.call(src, "rounding")) {
      const v = parseFloat(src.rounding);
      dst.rounding = Number.isFinite(v) ? v : 0;
    }
    if (Object.prototype.hasOwnProperty.call(src, "bank_commission")) {
      const v = parseFloat(src.bank_commission);
      dst.bank_commission = Number.isFinite(v) ? v : 0;
    }
  }
}

function buildFormDataFromFields(modelFields) {
  const newFormData = {};
  modelFields.forEach((field) => {
    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      newFormData[field.key] = field.defaultValue;
      return;
    }
    let defaultValue;
    switch (field.type) {
      case "array":
      case "group":
        defaultValue = field.isArray ? [] : {};
        break;
      case "boolean":
        defaultValue = false;
        break;
      case "number":
        defaultValue = 0;
        break;
      case "date":
        defaultValue = null;
        break;
      case "reference":
      case "list":
        defaultValue = field.isArray ? [] : null;
        break;
      case "file":
        defaultValue = null;
        break;
      default:
        defaultValue = "";
    }
    newFormData[field.key] = defaultValue;
  });
  return newFormData;
}

function contactRefValueFromDoc(contact) {
  if (contact == null || contact === "") return null;
  if (typeof contact === "object" && contact._id != null) {
    const name =
      (contact.name && String(contact.name).trim()) || String(contact._id);
    return { _id: contact._id, name };
  }
  const s = String(contact).trim();
  return /^[a-f0-9]{24}$/i.test(s) ? s : null;
}

function contactRefValueToId(val) {
  if (val == null || val === "") return "";
  if (typeof val === "object" && val._id != null) return String(val._id);
  const s = String(val).trim();
  return /^[a-f0-9]{24}$/i.test(s) ? s : "";
}

export default {
  name: "QuickRequestPaymentModal",
  components: { ContentSkeleton, Form2, FieldReference },
  props: {
    isVisible: { type: Boolean, default: false },
    /** `_id` del gasto (shipping_expenses). */
    expenseId: { type: String, default: null },
    expenseName: { type: String, default: "" },
    /** Pendiente máximo permitido (línea de balance / etapa 2). */
    amountMax: { type: Number, default: null },
    /**
     * Monto inicial sugerido (p. ej. anticipo proveedor % sobre factura), acotado al máximo en `open()`.
     */
    amountSuggested: { type: Number, default: null },
    /** Modo especial: divide pago de supplier invoice creando expense nuevo. */
    splitSupplierMode: { type: Boolean, default: false },
    /** Shipping donde se crea el nuevo `shipping_expenses` si no viene en el gasto origen. */
    splitShippingId: { type: String, default: null },
    lists: { type: Array, default: () => [] },
    extraParams: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      isSubmitting: false,
      modelFields: [],
      localVisualGroups: [],
      formData: {},
      models: [],
      modelDefinitions: {},
      expenseFileIsArray: false,
      /** Gasto (shipping_expenses) para mostrar `file` en modo vista. */
      expenseDocSnapshot: null,
      /** Contact seleccionado para el nuevo `shipping_expenses` en split mode. */
      splitContactRefValue: null,
      splitContactReferenceOptions: [],
    };
  },
  computed: {
    paymentBankAccountTenantLabel() {
      const raw = String(getCurrentClientDbName() || "").trim();
      if (!raw) return "";
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    },
    splitContactFieldDef() {
      return {
        type: "reference",
        ref: "contact",
        key: "contact",
        label: "Contact",
        preload: true,
        isArray: false,
        readonly: false,
        safe: false,
        hide: false,
      };
    },
    paymentAmountNumeric() {
      const raw =
        this.formData && Object.prototype.hasOwnProperty.call(this.formData, "amount")
          ? this.formData.amount
          : undefined;
      if (raw === null || raw === undefined || raw === "") return null;
      const n =
        typeof raw === "number"
          ? raw
          : parseFloat(String(raw).replace(/\s/g, "").replace(",", "."));
      return Number.isFinite(n) ? n : null;
    },
    appliedtoExtraRoundingNumeric() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return 0;
      const n = Number(at[0].rounding);
      return Number.isFinite(n) ? n : 0;
    },
    /** Total imputado al gasto (lo que baja el pendiente), que no siempre es el monto. */
    appliedToAmountNumeric() {
      const at = this.formData && this.formData.appliedto;
      if (Array.isArray(at) && at.length) {
        const sum = at.reduce((acc, row) => {
          const n = Number(row && typeof row === "object" ? row.amount : NaN);
          return acc + (Number.isFinite(n) ? n : 0);
        }, 0);
        return roundPaymentAmount(sum);
      }
      const amt = this.paymentAmountNumeric;
      return amt == null ? 0 : roundPaymentAmount(amt);
    },
    formattedAppliedToAmount() {
      return formatNumberSync(this.appliedToAmountNumeric, null, true);
    },
    paymentAppliedtoSummaryVisible() {
      if (this.loading) return false;
      const at = this.formData && this.formData.appliedto;
      return !!((Array.isArray(at) && at.length) || this.expenseName);
    },
    paymentAppliedtoExtrasVisible() {
      return (
        !this.loading &&
        this.formData &&
        Array.isArray(this.formData.appliedto) &&
        this.formData.appliedto.length > 0
      );
    },
    paymentAmountWithRoundingVisible() {
      return (
        this.paymentAppliedtoExtrasVisible &&
        this.appliedtoExtraRoundingNumeric > 0 &&
        this.paymentAmountNumeric != null
      );
    },
    formattedPaymentAmountWithRounding() {
      const amt = this.paymentAmountNumeric;
      if (amt == null) return null;
      const total = roundPaymentAmount(amt + this.appliedtoExtraRoundingNumeric);
      return formatNumberSync(total, null, true);
    },
    appliedtoExtraRoundingDisplay() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return "";
      const v = at[0].rounding;
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? n : "";
    },
    appliedtoExtraCommissionDisplay() {
      const at = this.formData && this.formData.appliedto;
      if (!Array.isArray(at) || !at[0] || typeof at[0] !== "object") return "";
      const v = at[0].bank_commission;
      if (v === null || v === undefined || v === "") return "";
      const n = Number(v);
      return Number.isFinite(n) ? n : "";
    },
    /** Tenants sin etapa de solicitud: al guardar, el pago ya queda pagado. */
    payNowMode() {
      return expensesArePaidWithoutRequest();
    },
    quickFields() {
      if (!this.modelFields || !this.modelFields.length) return [];
      const fieldKeys = this.payNowMode
        ? QUICK_PAY_NOW_FIELD_KEYS
        : QUICK_PAYMENT_FIELD_KEYS;
      return fieldKeys.map((key) => {
        let field = this.modelFields.find((f) => f.key === key);
        if (!field && key === "payment_request_file") {
          field = {
            key: "payment_request_file",
            label: "Payment request file",
            type: "file",
            isArray: true,
            hide: false,
            readonly: false,
            required: false,
          };
        }
        if (!field) return null;
        let overrides = {};
        if (key === "bankaccount") {
          overrides = { required: false, readonly: false };
        }
        if (key === "payment_date") {
          overrides = { required: true, readonly: false, hide: false, hidedisplay: false };
        }
        if (key === "amount") {
          // `safe` en la definición pone el candado: el monto nace bloqueado y en
          // «Divide payment» no se podía escribir (#94 fobtrading). Acá el monto es
          // lo que el usuario carga; ninguna regla lo calcula.
          overrides = {
            ...overrides,
            readonly: false,
            required: true,
            safe: false,
          };
          const cap = Number(this.amountMax);
          if (Number.isFinite(cap) && cap > 0) {
            overrides.max = cap;
          }
        }
        if (key === "payment_request_file") {
          overrides = {
            ...overrides,
            readonly: false,
            required: false,
            hide: false,
            hidedisplay: false,
          };
        }
        let label = field.label;
        if (key === "amount") {
          label = this.payNowMode
            ? this.$t("financialSummary.quickPayExpenseAmountLabel")
            : this.$t("financialSummary.quickRequestPaymentAmountLabel");
        } else if (key === "bankaccount") {
          const tenant = this.paymentBankAccountTenantLabel;
          label = tenant
            ? this.$t("financialSummary.quickAddCollectBankAccountLabel", { tenant })
            : this.$t("Bank account (from)");
        } else if (key === "payment_request_file") {
          label = this.$t("File");
        }
        return {
          ...field,
          ...overrides,
          label,
        };
      }).filter(Boolean);
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.open();
        }
      },
    },
  },
  methods: {
    async fetchModels() {
      const { data } = await wencesApi.get("/api/gotrading/model-definitions");
      this.models = data;
      const modelDefinitions = {};
      for (const model of data) {
        modelDefinitions[model.modelName] = model;
      }
      this.modelDefinitions = modelDefinitions;
    },
    async loadSplitContactReferenceOptions() {
      this.splitContactReferenceOptions = [];
      try {
        const params = new URLSearchParams();
        params.append("perPage", "1000");
        params.append("fields", "_id,name,label,textPlainVersion");
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/contact?${params.toString()}`
        );
        const items = (data && (data.items || data)) || [];
        this.splitContactReferenceOptions = Array.isArray(items)
          ? items.filter((x) => x && (x._id || x.name))
          : [];
      } catch (e) {
        console.warn("QuickRequestPaymentModal: contacts", e);
        this.splitContactReferenceOptions = [];
      }
    },
    onSplitContactInput(v) {
      this.splitContactRefValue = v;
    },
    applyAmountCapFromHook() {
      const cap = Number(this.amountMax);
      const hookAmt = Number(this.formData.amount);
      if (Number.isFinite(cap) && cap > 0) {
        const suggested =
          Number.isFinite(hookAmt) && hookAmt > 0 ? Math.min(hookAmt, cap) : cap;
        const amt = parseFloat(suggested.toFixed(2));
        const next = { ...this.formData, amount: amt };
        if (next.appliedto && Array.isArray(next.appliedto)) {
          next.appliedto = syncAppliedtoAmountsWithPaymentAmount(
            next.appliedto,
            amt
          );
        }
        if (Object.prototype.hasOwnProperty.call(next, "applied")) {
          next.applied = amt;
        }
        this.formData = next;
      } else if (
        Number.isFinite(hookAmt) &&
        hookAmt > 0 &&
        this.formData.appliedto &&
        Array.isArray(this.formData.appliedto) &&
        this.formData.appliedto.length > 0
      ) {
        const amt = hookAmt;
        const next = {
          ...this.formData,
          appliedto: syncAppliedtoAmountsWithPaymentAmount(
            this.formData.appliedto,
            amt
          ),
        };
        if (Object.prototype.hasOwnProperty.call(next, "applied")) {
          next.applied = amt;
        }
        this.formData = next;
      }
    },
    /** Tras hook + tope: si viene anticipo proveedor, el default es ese tramo (sin pasar el pendiente). */
    applyAmountSuggestedFromProp() {
      const sug = Number(this.amountSuggested);
      if (!Number.isFinite(sug) || sug < 0.01) return;
      const cap = Number(this.amountMax);
      let v = parseFloat(sug.toFixed(2));
      if (Number.isFinite(cap) && cap > 0 && v > cap) {
        v = parseFloat(cap.toFixed(2));
      }
      if (!this.formData || !Object.prototype.hasOwnProperty.call(this.formData, "amount")) {
        return;
      }
      const next = { ...this.formData, amount: v };
      if (next.appliedto && Array.isArray(next.appliedto)) {
        next.appliedto = syncAppliedtoAmountsWithPaymentAmount(next.appliedto, v);
      }
      if (Object.prototype.hasOwnProperty.call(next, "applied")) {
        next.applied = v;
      }
      this.formData = next;
    },
    async open() {
      if (!this.expenseId) return;
      this.loading = true;
      this.expenseDocSnapshot = null;
      this.splitContactRefValue = null;
      try {
        if (this.splitSupplierMode) {
          await this.loadSplitContactReferenceOptions();
        }
        await this.fetchModels();
        try {
          const { data: exp } = await wencesApi.get(
            `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(String(this.expenseId))}`
          );
          this.expenseDocSnapshot = exp && exp._id ? exp : null;
          try {
            const { data: shipDef } = await wencesApi.get(
              "/api/gotrading/model-definitions/shipping"
            );
            const fileDef = fileFieldDefFromShippingDefinition(
              shipDef && shipDef.fields
            );
            this.expenseFileIsArray = !!fileDef.isArray;
          } catch (defErr) {
            console.warn("QuickRequestPaymentModal: shipping model def", defErr);
            this.expenseFileIsArray = false;
          }
          if (this.splitSupplierMode && this.expenseDocSnapshot) {
            this.splitContactRefValue = contactRefValueFromDoc(
              this.expenseDocSnapshot.contact
            );
          }
        } catch (e) {
          console.warn("QuickRequestPaymentModal: load expense for file", e);
          this.expenseDocSnapshot = null;
        }
        const extraParamsString =
          this.extraParams && typeof this.extraParams === "object"
            ? Object.entries(this.extraParams)
                .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                .join("&")
            : "";

        const url = `/api/gotrading/model-definitions/payment?parentID=${encodeURIComponent(
          String(this.expenseId)
        )}${extraParamsString ? "&" + extraParamsString : ""}&parentModel=${encodeURIComponent(
          PARENT_MODEL
        )}`;
        const { data } = await wencesApi.get(url);

        if (data && data._operationMessages && Array.isArray(data._operationMessages)) {
          for (const msg of data._operationMessages) {
            await Swal.fire({
              icon: msg.type === "success" ? "success" : "info",
              title:
                msg.type === "success"
                  ? this.$tn("success")
                  : this.$tn("financialSummary.quickAddCollectInfoTitle"),
              text: msg.message,
              confirmButtonText: this.$tn("financialSummary.quickAddCollectUnderstood"),
            });
          }
        }

        if (data.redirect) {
          this.$router.push(`/${data.redirect}`);
          this.close();
          return;
        }

        if (data && data.visualGroups) {
          this.localVisualGroups = data.visualGroups;
          this.modelFields = data.fields || [];
        } else {
          this.modelFields = [];
          this.localVisualGroups = [];
        }

        this.formData = buildFormDataFromFields(this.modelFields);
        if (this.modelFields.some((f) => f.key === "amount")) {
          this.applyAmountCapFromHook();
          this.applyAmountSuggestedFromProp();
        }
        // Se paga hoy salvo que se cambie: el pago nace pagado, no puede quedar sin fecha.
        if (this.payNowMode && !toYyyyMmDdSync(this.formData.payment_date)) {
          this.formData = {
            ...this.formData,
            payment_date: calendarTodayLocalYyyyMmDd(),
          };
        }
      } catch (e) {
        console.error("QuickRequestPaymentModal open:", e);
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.quickRequestPaymentOpenFailed"),
        });
        this.close();
      } finally {
        this.loading = false;
      }
    },
    onFormInput(val) {
      if (val && typeof val === "object") {
        let next = { ...this.formData, ...val };
        if (
          next.appliedto &&
          Array.isArray(next.appliedto) &&
          next.appliedto.length > 0
        ) {
          const amt = Number(next.amount);
          if (Number.isFinite(amt) && amt >= 0) {
            next = {
              ...next,
              appliedto: syncAppliedtoAmountsWithPaymentAmount(
                next.appliedto,
                amt
              ),
            };
            if (Object.prototype.hasOwnProperty.call(next, "applied")) {
              next.applied = amt;
            }
          }
        }
        this.formData = next;
      } else {
        this.formData = {};
      }
    },
    setAppliedtoRoundingAndCommission(partial) {
      if (!partial || typeof partial !== "object") return;
      const at = this.formData.appliedto;
      if (!Array.isArray(at) || !at.length) return;
      const first = at[0] && typeof at[0] === "object" ? { ...at[0] } : {};
      const rOk = Object.prototype.hasOwnProperty.call(partial, "rounding")
        ? parseFloat(Number(partial.rounding).toFixed(2)) || 0
        : parseFloat(first.rounding) || 0;
      const cOk = Object.prototype.hasOwnProperty.call(partial, "bank_commission")
        ? parseFloat(Number(partial.bank_commission).toFixed(2)) || 0
        : parseFloat(first.bank_commission) || 0;
      const next = at.map((row, i) => {
        const base = typeof row === "object" && row ? { ...row } : {};
        if (i === 0) {
          return { ...base, rounding: rOk, bank_commission: cOk };
        }
        return { ...base, rounding: 0, bank_commission: 0 };
      });
      this.$set(this.formData, "appliedto", next);
    },
    onAppliedtoExtraRoundingInput(ev) {
      const raw = (ev && ev.target && ev.target.value) || "";
      const n = raw === "" ? 0 : parseFloat(String(raw).replace(",", "."));
      this.setAppliedtoRoundingAndCommission({
        rounding: Number.isFinite(n) ? n : 0,
      });
    },
    onAppliedtoExtraCommissionInput(ev) {
      const raw = (ev && ev.target && ev.target.value) || "";
      const n = raw === "" ? 0 : parseFloat(String(raw).replace(",", "."));
      this.setAppliedtoRoundingAndCommission({
        bank_commission: Number.isFinite(n) ? n : 0,
      });
    },
    async submit() {
      const fromChild =
        this.$refs.paymentFieldsForm && this.$refs.paymentFieldsForm.formData
          ? this.$refs.paymentFieldsForm.formData
          : {};
      let mergedPre = { ...this.formData, ...fromChild };

      if (
        mergedPre.appliedto &&
        Array.isArray(mergedPre.appliedto) &&
        mergedPre.appliedto.length > 0
      ) {
        const amtPre = Number(mergedPre.amount);
        if (Number.isFinite(amtPre)) {
          mergedPre = {
            ...mergedPre,
            appliedto: syncAppliedtoAmountsWithPaymentAmount(
              mergedPre.appliedto,
              amtPre
            ),
          };
          if (Object.prototype.hasOwnProperty.call(mergedPre, "applied")) {
            mergedPre.applied = amtPre;
          }
        }
      }

      const amt = Number(mergedPre.amount);
      if (!Number.isFinite(amt) || amt < 0.01) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.quickRequestPaymentAmountInvalidTitle"),
          text: this.$tn("financialSummary.quickRequestPaymentAmountInvalidText"),
        });
        return;
      }
      const cap = Number(this.amountMax);
      if (Number.isFinite(cap) && cap > 0 && amt > cap + 0.02) {
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.quickRequestPaymentAmountTooMuchTitle"),
          text: this.$tn("financialSummary.quickRequestPaymentAmountTooMuchText", {
            max: "$" + formatNumberSync(cap, null, true),
          }),
        });
        return;
      }

      if (this.isSubmitting) return;
      this.isSubmitting = true;
      try {
        let merged = { ...mergedPre };
        if (
          merged.appliedto &&
          Array.isArray(merged.appliedto) &&
          merged.appliedto.length > 0
        ) {
          const amtM = Number(merged.amount);
          if (Number.isFinite(amtM)) {
            merged = {
              ...merged,
              appliedto: syncAppliedtoAmountsWithPaymentAmount(
                merged.appliedto,
                amtM
              ),
            };
            if (Object.prototype.hasOwnProperty.call(merged, "applied")) {
              merged.applied = amtM;
            }
          }
        }
        // Extras viven en formData (inputs fuera de Form2); fromChild puede no traerlos.
        mergePaymentAppliedtoExtrasIntoPayload(this.formData, merged);

        let dataToSend = processReferenceFormData(
          merged,
          this.modelFields,
          this.localVisualGroups
        );
        mergePaymentAppliedtoExtrasIntoPayload(merged, dataToSend);
        delete dataToSend.idModel;
        delete dataToSend.undefined;
        delete dataToSend.createdBy;
        delete dataToSend.updatedBy;

        removeReferenceGroupIds(dataToSend, this.modelFields);

        // Pago directo: no hay etapa de solicitud, se guarda ya como pagado.
        if (this.payNowMode) {
          const payDate = toYyyyMmDdSync(dataToSend.payment_date);
          if (!payDate) {
            await Swal.fire({
              icon: "warning",
              title: this.$tn("financialSummary.quickPayExpenseDateRequiredTitle"),
              text: this.$tn("financialSummary.quickPayExpenseDateRequiredText"),
            });
            return;
          }
          dataToSend.payment_date = payDate;
          dataToSend.paid = true;
          dataToSend.estimated_paymentdate = null;
          // No hubo solicitud: la fecha de solicitud es la del pago. Si se deja
          // en hoy, pagar con fecha anterior lo rechaza la validación de payment.
          dataToSend.date = payDate;
        }

        let targetExpenseId = this.expenseId;
        if (this.splitSupplierMode) {
          const splitContactId = contactRefValueToId(this.splitContactRefValue);
          if (!splitContactId) {
            await Swal.fire({
              icon: "warning",
              title: this.$tn("financialSummary.quickRequestPaymentSplitContactRequiredTitle"),
              text: this.$tn("financialSummary.quickRequestPaymentSplitContactRequiredText"),
            });
            return;
          }
          const source = this.expenseDocSnapshot;
          if (!source || !source._id) {
            throw new Error(
              this.$t("financialSummary.quickRequestPaymentSplitSourceMissing")
            );
          }
          const sourceParent =
            source.parent != null && source.parent !== ""
              ? source.parent
              : this.splitShippingId;
          if (!sourceParent) {
            throw new Error(
              this.$t("financialSummary.quickRequestPaymentSplitParentMissing")
            );
          }
          const sourceAmount = Number(source.amount);
          if (!Number.isFinite(sourceAmount) || sourceAmount < 0.01) {
            throw new Error(
              this.$t("financialSummary.quickRequestPaymentSplitSourceAmountInvalid")
            );
          }
          const nextSourceAmount = parseFloat((sourceAmount - amt).toFixed(2));
          if (!Number.isFinite(nextSourceAmount) || nextSourceAmount < -0.000001) {
            throw new Error(
              this.$t("financialSummary.quickRequestPaymentSplitAmountTooHigh")
            );
          }
          const splitExpensePayload = {
            parent: sourceParent,
            type: source.type,
            amount: amt,
            contact: splitContactId,
            // El back descuenta este tramo del gasto original cada vez que lo recalcula.
            split_from: String(source._id),
          };
          if (source.currencyex != null && source.currencyex !== "") {
            splitExpensePayload.currencyex = source.currencyex;
          }
          if (source.date != null && source.date !== "") {
            splitExpensePayload.date = source.date;
          }
          if (source.invoice != null && source.invoice !== "") {
            splitExpensePayload.invoice = source.invoice;
          }
          const serializedFile = serializeExpenseFileForApi(
            source.file,
            this.expenseFileIsArray
          );
          if (serializedFile != null) {
            splitExpensePayload.file = serializedFile;
          }
          const sourceExpenseUrl = `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(
            String(source._id)
          )}`;
          // Primero el original y después el tramo: al guardar el tramo el back recalcula
          // el original (total de la etapa menos lo dividido) y ese valor tiene que quedar.
          await wencesApi.put(sourceExpenseUrl, {
            partialUpdate: true,
            amount: Math.max(0, nextSourceAmount),
          });
          let createdExpense = null;
          try {
            const createdExpenseResp = await wencesApi.post(
              "/api/gotrading/dynamic/shipping_expenses",
              splitExpensePayload
            );
            createdExpense =
              createdExpenseResp && createdExpenseResp.data
                ? createdExpenseResp.data
                : null;
          } catch (createError) {
            createdExpense = null;
            console.error("QuickRequestPaymentModal split create:", createError);
          }
          if (!createdExpense || !createdExpense._id) {
            await wencesApi
              .put(sourceExpenseUrl, { partialUpdate: true, amount: sourceAmount })
              .catch((e) => console.error("QuickRequestPaymentModal split restore:", e));
            throw new Error(
              this.$t("financialSummary.quickRequestPaymentSplitCreateExpenseFailed")
            );
          }
          targetExpenseId = String(createdExpense._id);
          if (
            dataToSend.appliedto &&
            Array.isArray(dataToSend.appliedto) &&
            dataToSend.appliedto.length
          ) {
            dataToSend.appliedto = dataToSend.appliedto.map((row) => {
              if (!row || typeof row !== "object") return row;
              return {
                ...row,
                expense: targetExpenseId,
              };
            });
          }
          if (Object.prototype.hasOwnProperty.call(dataToSend, "contact")) {
            dataToSend.contact = splitContactId;
          }
        }
        dataToSend.parentID = targetExpenseId;
        dataToSend.parentModel = PARENT_MODEL;

        const response = await saveConfirmingContactOutsideOperation(
          (key, params) => this.$t(key, params),
          (forceSave) =>
            wencesApi.post(
              `/api/gotrading/dynamic/payment`,
              forceSave ? { ...dataToSend, forceSave: true } : dataToSend
            )
        );
        if (!response) return;

        await Swal.fire({
          title: this.payNowMode
            ? this.$tn("financialSummary.quickPayExpenseSuccess")
            : this.$tn("financialSummary.payments.paymentCreatedSuccessfully"),
          icon: "success",
        });
        this.$emit("saved", response.data);
        this.close();
      } catch (error) {
        console.error("QuickRequestPaymentModal submit:", error);
        const errorData = error.response && error.response.data;
        let errorMessage = this.$t("financialSummary.payments.failedToSavePayment");
        if (errorData) {
          if (errorData.error) errorMessage = errorData.error;
          else if (errorData.message) errorMessage = errorData.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: errorMessage,
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    close() {
      this.formData = {};
      this.modelFields = [];
      this.localVisualGroups = [];
      this.expenseDocSnapshot = null;
      this.splitContactRefValue = null;
      this.splitContactReferenceOptions = [];
      this.loading = false;
      this.isSubmitting = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
.modal.show {
  display: block;
}
</style>

<!-- Sin scoped: las columnas las arma Form2, que es otro componente. -->
<style>
/* Un campo por renglón: cuatro columnas en el ancho del popup no se leían. */
.quick-payment-fields .form2-field-row > [class*="col-"] {
  flex: 0 0 100%;
  max-width: 100%;
}
</style>
